import React from "react";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

/**
 * Cloudflare Turnstile widget.
 * Uses execution=execute so verification only starts after the visitor clicks
 * (Managed widgets otherwise auto-succeed on page load for low-risk browsers).
 *
 * Note: Network 401s on `/cdn-cgi/challenge-platform/.../pat/...` are expected
 * (Private Access Token probe). They are NOT a sitekey/domain failure.
 * @see https://developers.cloudflare.com/cloudflare-challenges/reference/private-access-tokens/
 */
export default function AuthTurnstile({ siteKey, onToken, onExpire, onError }) {
  const theme = useTheme();
  const containerRef = React.useRef(null);
  const widgetIdRef = React.useRef(null);
  const [softError, setSoftError] = React.useState("");
  const [ready, setReady] = React.useState(false);
  const [started, setStarted] = React.useState(false);
  const [solved, setSolved] = React.useState(false);

  const onTokenRef = React.useRef(onToken);
  const onExpireRef = React.useRef(onExpire);
  const onErrorRef = React.useRef(onError);
  React.useEffect(() => {
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
  }, [onToken, onExpire, onError]);

  React.useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;

    let cancelled = false;
    setSoftError("");
    setReady(false);
    setStarted(false);
    setSolved(false);

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current != null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
      containerRef.current.innerHTML = "";
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        execution: "execute",
        appearance: "always",
        callback: (token) => {
          setSoftError("");
          setSolved(true);
          onTokenRef.current?.(token);
        },
        "expired-callback": () => {
          setSolved(false);
          setStarted(false);
          onTokenRef.current?.("");
          onExpireRef.current?.();
        },
        "error-callback": (code) => {
          setSolved(false);
          setStarted(false);
          onTokenRef.current?.("");
          onExpireRef.current?.();
          setSoftError(
            code
              ? `Verification busy (${code}). Click the box to try again.`
              : "Verification failed. Click the box to try again.",
          );
          onErrorRef.current?.(new Error(String(code || "turnstile_error")));
        },
        theme: theme.palette.mode === "dark" ? "dark" : "light",
        retry: "auto",
      });
      if (!cancelled) setReady(true);
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const existing = document.querySelector('script[data-turnstile="1"]');
      if (!existing) {
        const script = document.createElement("script");
        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.dataset.turnstile = "1";
        script.onload = renderWidget;
        document.head.appendChild(script);
      } else if (window.turnstile) {
        renderWidget();
      } else {
        existing.addEventListener("load", renderWidget);
      }
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme.palette.mode]);

  const runChallenge = React.useCallback(() => {
    if (!ready || solved || !window.turnstile || widgetIdRef.current == null) {
      return;
    }
    setStarted(true);
    setSoftError("");
    try {
      window.turnstile.execute(widgetIdRef.current);
    } catch {
      try {
        window.turnstile.reset(widgetIdRef.current);
        window.turnstile.execute(widgetIdRef.current);
      } catch (err) {
        setStarted(false);
        setSoftError("Could not start verification. Refresh and try again.");
        onErrorRef.current?.(err instanceof Error ? err : new Error("execute_failed"));
      }
    }
  }, [ready, solved]);

  if (!siteKey) return null;

  const showClickPrompt = ready && !started && !solved;

  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        onClick={showClickPrompt ? runChallenge : undefined}
        onKeyDown={
          showClickPrompt
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  runChallenge();
                }
              }
            : undefined
        }
        role={showClickPrompt ? "button" : undefined}
        tabIndex={showClickPrompt ? 0 : undefined}
        aria-label={showClickPrompt ? "Click to verify you are human" : undefined}
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          minHeight: 72,
          cursor: showClickPrompt ? "pointer" : "default",
          borderRadius: 2,
          outline: "none",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          ...(showClickPrompt
            ? {
                "&:hover": { transform: "translateY(-1px)" },
                "&:focus-visible": {
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
              }
            : {}),
          "& iframe": { maxWidth: "100%" },
        }}
      >
        <Box ref={containerRef} sx={{ display: "flex", justifyContent: "center" }} />
        {showClickPrompt ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              border: `2px solid ${
                isDark ? alpha("#fff", 0.55) : alpha("#111", 0.35)
              }`,
              bgcolor: isDark ? "#1a1a1a" : "#f7f7f8",
              boxShadow: isDark
                ? `0 0 0 1px ${alpha("#fff", 0.08)}, 0 8px 24px ${alpha("#000", 0.45)}`
                : `0 1px 2px ${alpha("#000", 0.06)}, 0 8px 20px ${alpha("#000", 0.08)}`,
              pointerEvents: "none",
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 1,
                border: `2px solid ${isDark ? alpha("#fff", 0.85) : alpha("#111", 0.7)}`,
                bgcolor: isDark ? alpha("#fff", 0.06) : "#fff",
                boxShadow: `inset 0 0 0 1px ${alpha("#000", 0.12)}`,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? "#fff" : "#111",
                  fontWeight: 700,
                  lineHeight: 1.25,
                }}
              >
                Verify you are human
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.25,
                  color: isDark ? alpha("#fff", 0.72) : alpha("#111", 0.6),
                  fontWeight: 500,
                }}
              >
                Click this box to continue
              </Typography>
            </Box>
            <VerifiedUserOutlinedIcon
              sx={{
                fontSize: 22,
                color: isDark ? alpha("#fff", 0.55) : alpha("#111", 0.4),
                flexShrink: 0,
              }}
            />
          </Box>
        ) : null}
      </Box>
      {softError ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.75,
            color: alpha(theme.palette.warning.main, 0.95),
            textAlign: "center",
          }}
        >
          {softError}
        </Typography>
      ) : null}
    </Box>
  );
}
